import { useMemo, useState } from "react";
import { useQuery } from "convex/react";

import { queryRef } from "@/lib/convex/refs";
import { onboardingAnalyticsDashboardSecret } from "@/lib/env";
import {
  clearDashboardAuth,
  ONBOARDING_ANALYTICS_TEST_DEVICE_ID,
  readDashboardAuth,
  readExcludeTestDevice,
  writeDashboardAuth,
  writeExcludeTestDevice,
} from "./onboardingAnalyticsAuth";

type PlatformFilter = "all" | "ios" | "android" | "web";

type RevenueByCurrency = {
  currencyCode: string;
  total: number;
  count: number;
};

type FunnelStep = {
  step: number;
  screenId: string;
  sessionsReached: number;
  uniqueDevices: number;
  dropOffFromPrevious: number | null;
  dropOffPercent: number | null;
};

type PaywallMetrics = {
  sessionsReachedPaywall: number;
  uniqueDevicesReachedPaywall: number;
  trialStarted: number;
  lifetimeActivated: number;
  pendingTrialDevices: number;
  realizedRevenue: RevenueByCurrency[];
  pendingTrialValue: RevenueByCurrency[];
  restoreSucceeded: number;
  purchaseErrors: number;
  purchaseCancelled: number;
};

type TabGateSummary = {
  sessionsStarted: number;
  uniqueDevicesStarted: number;
  gateDismissals: number;
  gateUnlocks: number;
  byRequestedTab: Array<{ tab: string; sessions: number; devices: number }>;
};

type DashboardSummary = {
  onboardingFunnel: FunnelStep[];
  tabGateFunnel: FunnelStep[];
  tabGateSummary: TabGateSummary;
  tabGatePaywall: PaywallMetrics;
  onboardingTimeSeries: Array<{
    date: string;
    screens: Array<{ screenId: string; step: number; uniqueDevices: number }>;
  }>;
  tabGateTimeSeries: Array<{
    date: string;
    screens: Array<{ screenId: string; step: number; uniqueDevices: number }>;
  }>;
};

const getDashboardSummaryRef = queryRef<
  {
    dashboardSecret: string;
    startMs: number;
    endMs: number;
    platform: PlatformFilter;
    excludeDeviceIds?: string[];
  },
  DashboardSummary
>("onboardingAnalytics:getDashboardSummary");

function defaultRange() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - 13);
  start.setHours(0, 0, 0, 0);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

function formatDateInput(ms: number) {
  return new Date(ms).toISOString().slice(0, 10);
}

function parseDateInput(value: string, endOfDay: boolean) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  }
  return date.getTime();
}

function formatCurrencyTotal(currencyCode: string, total: number) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
    }).format(total);
  } catch {
    return `${currencyCode} ${total.toFixed(2)}`;
  }
}

function FunnelBar({ value, max }: { value: number; max: number }) {
  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 flex-1 rounded-full bg-[oklch(22%_0.03_160)]">
      <div
        className="h-2 rounded-full bg-[oklch(68%_0.12_75)]"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function RevenueList({
  title,
  rows,
  emptyLabel,
}: {
  title: string;
  rows: RevenueByCurrency[];
  emptyLabel: string;
}) {
  return (
    <div>
      <h3 className="text-sm font-medium text-[oklch(72%_0.025_155)]">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-[oklch(58%_0.02_155)]">{emptyLabel}</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((row) => (
            <li key={row.currencyCode} className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-[oklch(68%_0.025_155)]">{row.currencyCode}</span>
              <span className="tabular-nums">
                {formatCurrencyTotal(row.currencyCode, row.total)}
                <span className="ml-2 text-[oklch(58%_0.02_155)]">({row.count})</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FunnelTable({ title, rows, max }: { title: string; rows: FunnelStep[]; max: number }) {
  return (
    <section className="liquid-glass-surface overflow-x-auto p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      <table className="mt-3 w-full min-w-[640px] text-left text-sm">
        <thead className="text-[oklch(68%_0.025_155)]">
          <tr>
            <th className="py-2 pr-3">Step</th>
            <th className="py-2 pr-3">Screen</th>
            <th className="py-2 pr-3">Sessions</th>
            <th className="py-2 pr-3">Devices</th>
            <th className="py-2 pr-3">Drop-off</th>
            <th className="py-2">Reach</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${title}-${row.step}`} className="border-t border-[oklch(100%_0_0_/_0.06)]">
              <td className="py-2 pr-3 font-mono">{row.step}</td>
              <td className="py-2 pr-3">{row.screenId}</td>
              <td className="py-2 pr-3">{row.sessionsReached}</td>
              <td className="py-2 pr-3">{row.uniqueDevices}</td>
              <td className="py-2 pr-3">
                {row.dropOffFromPrevious == null
                  ? "—"
                  : `${row.dropOffFromPrevious} (${row.dropOffPercent ?? 0}%)`}
              </td>
              <td className="py-2">
                <div className="flex items-center gap-2">
                  <FunnelBar value={row.sessionsReached} max={max} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function TimeSeriesSection({
  title,
  days,
  max,
}: {
  title: string;
  days: DashboardSummary["onboardingTimeSeries"];
  max: number;
}) {
  return (
    <section className="liquid-glass-surface overflow-x-auto p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="mt-4 space-y-4">
        {days.map((day) => (
          <div key={day.date}>
            <p className="mb-2 text-xs font-medium text-[oklch(68%_0.025_155)]">{day.date}</p>
            <div className="grid gap-2">
              {day.screens
                .filter((screen) => screen.uniqueDevices > 0)
                .map((screen) => (
                  <div key={`${day.date}-${screen.step}`} className="flex items-center gap-2 text-xs">
                    <span className="w-28 shrink-0 truncate">{screen.screenId}</span>
                    <FunnelBar value={screen.uniqueDevices} max={max} />
                    <span className="w-8 text-right tabular-nums">{screen.uniqueDevices}</span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PaywallMetricsSection({
  title,
  metrics,
}: {
  title: string;
  metrics: PaywallMetrics;
}) {
  return (
    <section className="liquid-glass-surface p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Sessions reached paywall</dt>
          <dd className="text-xl font-semibold">{metrics.sessionsReachedPaywall}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Unique devices at paywall</dt>
          <dd className="text-xl font-semibold">{metrics.uniqueDevicesReachedPaywall}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Yearly trial started (devices)</dt>
          <dd className="text-xl font-semibold">{metrics.trialStarted}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Lifetime activated (devices)</dt>
          <dd className="text-xl font-semibold">{metrics.lifetimeActivated}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Active trial devices</dt>
          <dd className="text-xl font-semibold">{metrics.pendingTrialDevices}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Restore succeeded</dt>
          <dd>{metrics.restoreSucceeded}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Purchase errors</dt>
          <dd>{metrics.purchaseErrors}</dd>
        </div>
        <div>
          <dt className="text-[oklch(68%_0.025_155)]">Purchase cancelled</dt>
          <dd>{metrics.purchaseCancelled}</dd>
        </div>
      </dl>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <RevenueList
          title="Realized revenue (paid)"
          rows={metrics.realizedRevenue}
          emptyLabel="No realized purchases in range."
        />
        <RevenueList
          title="Pending trial value (not earned)"
          rows={metrics.pendingTrialValue}
          emptyLabel="No pending trials in range."
        />
      </div>
    </section>
  );
}

export function OnboardingAnalyticsDashboardPage() {
  const initialRange = useMemo(() => defaultRange(), []);
  const [secret, setSecret] = useState(() => readDashboardAuth() ?? "");
  const [draftSecret, setDraftSecret] = useState("");
  const [startDate, setStartDate] = useState(formatDateInput(initialRange.startMs));
  const [endDate, setEndDate] = useState(formatDateInput(initialRange.endMs));
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [excludeTestDevice, setExcludeTestDevice] = useState(() => readExcludeTestDevice());

  const startMs = parseDateInput(startDate, false) ?? initialRange.startMs;
  const endMs = parseDateInput(endDate, true) ?? initialRange.endMs;
  const authedSecret = secret.trim();
  const excludeDeviceIds = excludeTestDevice ? [ONBOARDING_ANALYTICS_TEST_DEVICE_ID] : undefined;

  const summary = useQuery(
    getDashboardSummaryRef,
    authedSecret
      ? {
          dashboardSecret: authedSecret,
          startMs,
          endMs,
          platform,
          excludeDeviceIds,
        }
      : "skip",
  );

  const configured = Boolean(onboardingAnalyticsDashboardSecret.trim());

  if (!configured) {
    return (
      <main className="mx-auto max-w-3xl p-6 text-[oklch(88%_0.02_150)]">
        <h1 className="text-xl font-semibold">Onboarding analytics</h1>
        <p className="mt-3 text-sm leading-6 text-[oklch(72%_0.025_155)]">
          Dashboard is not configured. Set{" "}
          <code className="text-[oklch(78%_0.06_155)]">VITE_ONBOARDING_ANALYTICS_DASHBOARD_SECRET</code>{" "}
          in the frontend env and{" "}
          <code className="text-[oklch(78%_0.06_155)]">ONBOARDING_ANALYTICS_DASHBOARD_SECRET</code>{" "}
          on the Convex deployment.
        </p>
      </main>
    );
  }

  if (!authedSecret) {
    return (
      <main className="mx-auto flex min-h-[100svh] max-w-md flex-col justify-center gap-4 p-6 text-[oklch(88%_0.02_150)]">
        <h1 className="text-xl font-semibold">Onboarding analytics</h1>
        <p className="text-sm text-[oklch(72%_0.025_155)]">Owner access only.</p>
        <input
          type="password"
          className="rounded-xl border border-[oklch(100%_0_0_/_0.12)] bg-[oklch(14%_0.025_160)] px-3 py-2 text-sm"
          placeholder="Dashboard secret"
          value={draftSecret}
          onChange={(event) => setDraftSecret(event.target.value)}
        />
        <button
          type="button"
          className="primary-action"
          onClick={() => {
            const next = draftSecret.trim();
            if (!next) return;
            writeDashboardAuth(next);
            setSecret(next);
          }}
        >
          Unlock
        </button>
      </main>
    );
  }

  const onboardingFunnelMax = Math.max(
    1,
    ...(summary?.onboardingFunnel.map((row) => row.sessionsReached) ?? [1]),
  );
  const tabGateFunnelMax = Math.max(
    1,
    ...(summary?.tabGateFunnel.map((row) => row.sessionsReached) ?? [1]),
  );
  const onboardingTimeSeriesMax = Math.max(
    1,
    ...(summary?.onboardingTimeSeries.flatMap((day) => day.screens.map((screen) => screen.uniqueDevices)) ?? [1]),
  );
  const tabGateTimeSeriesMax = Math.max(
    1,
    ...(summary?.tabGateTimeSeries.flatMap((day) => day.screens.map((screen) => screen.uniqueDevices)) ?? [1]),
  );

  return (
    <main className="mx-auto max-w-5xl space-y-8 p-6 pb-16 text-[oklch(88%_0.02_150)]">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Onboarding & tab paywall analytics</h1>
          <p className="mt-1 text-sm text-[oklch(68%_0.025_155)]">
            Onboarding funnel (7 steps) and tab-paywall gate funnel (3 steps) are tracked separately.
          </p>
        </div>
        <button
          type="button"
          className="text-xs text-[oklch(68%_0.025_155)] underline"
          onClick={() => {
            clearDashboardAuth();
            setSecret("");
            setDraftSecret("");
          }}
        >
          Lock dashboard
        </button>
      </header>

      <section className="liquid-glass-surface grid gap-3 p-4 sm:grid-cols-4">
        <label className="grid gap-1 text-xs">
          <span className="text-[oklch(68%_0.025_155)]">From</span>
          <input
            type="date"
            className="rounded-lg bg-[oklch(14%_0.025_160)] px-2 py-1.5"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="grid gap-1 text-xs">
          <span className="text-[oklch(68%_0.025_155)]">To</span>
          <input
            type="date"
            className="rounded-lg bg-[oklch(14%_0.025_160)] px-2 py-1.5"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
        <label className="grid gap-1 text-xs sm:col-span-2">
          <span className="text-[oklch(68%_0.025_155)]">Platform</span>
          <select
            className="rounded-lg bg-[oklch(14%_0.025_160)] px-2 py-1.5"
            value={platform}
            onChange={(event) => setPlatform(event.target.value as PlatformFilter)}
          >
            <option value="all">All</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
            <option value="web">Web</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs sm:col-span-4">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-[oklch(100%_0_0_/_0.2)]"
            checked={excludeTestDevice}
            onChange={(event) => {
              const next = event.target.checked;
              setExcludeTestDevice(next);
              writeExcludeTestDevice(next);
            }}
          />
          <span>Exclude my test device from all metrics</span>
        </label>
      </section>

      {summary === undefined ? (
        <p className="text-sm text-[oklch(68%_0.025_155)]">Loading aggregates…</p>
      ) : null}

      {summary && (
        <>
          <FunnelTable
            title="Onboarding funnel (7 steps)"
            rows={summary.onboardingFunnel}
            max={onboardingFunnelMax}
          />

          <section className="liquid-glass-surface p-4">
            <h2 className="text-lg font-semibold">Tab paywall gate — summary</h2>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-[oklch(68%_0.025_155)]">Gate sessions started</dt>
                <dd className="text-xl font-semibold">{summary.tabGateSummary.sessionsStarted}</dd>
              </div>
              <div>
                <dt className="text-[oklch(68%_0.025_155)]">Unique devices</dt>
                <dd className="text-xl font-semibold">{summary.tabGateSummary.uniqueDevicesStarted}</dd>
              </div>
              <div>
                <dt className="text-[oklch(68%_0.025_155)]">Dismissals</dt>
                <dd className="text-xl font-semibold">{summary.tabGateSummary.gateDismissals}</dd>
              </div>
              <div>
                <dt className="text-[oklch(68%_0.025_155)]">Unlocks (purchase/restore)</dt>
                <dd className="text-xl font-semibold">{summary.tabGateSummary.gateUnlocks}</dd>
              </div>
            </dl>
            {summary.tabGateSummary.byRequestedTab.length > 0 ? (
              <table className="mt-4 w-full text-left text-sm">
                <thead className="text-[oklch(68%_0.025_155)]">
                  <tr>
                    <th className="py-2 pr-3">Requested tab</th>
                    <th className="py-2 pr-3">Sessions</th>
                    <th className="py-2">Devices</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.tabGateSummary.byRequestedTab.map((row) => (
                    <tr key={row.tab} className="border-t border-[oklch(100%_0_0_/_0.06)]">
                      <td className="py-2 pr-3 font-mono">{row.tab}</td>
                      <td className="py-2 pr-3">{row.sessions}</td>
                      <td className="py-2">{row.devices}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </section>

          <FunnelTable
            title="Tab paywall funnel (trial → notifications → paywall)"
            rows={summary.tabGateFunnel}
            max={tabGateFunnelMax}
          />

          <PaywallMetricsSection title="Tab paywall revenue" metrics={summary.tabGatePaywall} />

          <TimeSeriesSection
            title="Onboarding time series"
            days={summary.onboardingTimeSeries}
            max={onboardingTimeSeriesMax}
          />

          <TimeSeriesSection
            title="Tab paywall time series"
            days={summary.tabGateTimeSeries}
            max={tabGateTimeSeriesMax}
          />
        </>
      )}
    </main>
  );
}
